// src/utils/invoicing/invoiceBuilder.js
//
// Pure data layer — no PDF, no XML.
// Takes raw deliveries + member array, returns structured InvoiceData objects
// that both the PDF and Finvoice renderers consume.
//
// InvoiceData shape:
// {
//   invoiceNumber : string
//   issuedAt      : number  (ms)
//   dueAt         : number  (ms, issuedAt + 14 days)
//   buyer         : { companyName, yNumber, reference, department, email }
//   rows          : [{ id, date, origin, destination, unitPrice }]
//   billingFee    : number | null
//   net           : number   (rows total + billingFee)
//   alvRate       : number   (e.g. 25.5)
//   alvAmount     : number
//   gross         : number
// }

const DUE_DAYS = 14;

function makeInvoiceNumber(prefix, yNumber) {
  const datePart = new Date().toISOString().slice(0, 10).replace(/-/g, '');
  const rand     = Math.floor(Math.random() * 900 + 100);
  const tag      = (yNumber || 'x').replace(/[^a-zA-Z0-9]/g, '').slice(0, 6);
  return `${prefix}-${datePart}-${tag}-${rand}`;
}

// ─── Outgoing (org → senders) ─────────────────────────────────────────────────

/**
 * Groups completed deliveries by their billing party and attaches the
 * per-sender billingFee from member settings.
 *
 * @param {object[]} deliveries  - completed delivery documents
 * @param {object[]} senders     - member documents (billingDetails, yNumber, etc.)
 * @param {number}   alvRate     - VAT rate to apply (e.g. 25.5)
 * @returns {Array<{ invoiceData: InvoiceData, skipped: boolean, reason: string }>}
 */
export function buildOutgoingGroups(deliveries, senders, alvRate = 25.5) {
  const groups = {};

  for (const d of deliveries) {
    const member = senders.find((m) => m.userId === d.senderId);
    const bd     = member?.billing;

    // Delivery-level billing overrides take priority, then fall back to member settings.
    const key = d.billing?.yNumber || d.senderId || 'unknown';

    if (!groups[key]) {
      groups[key] = {
        skipped:    bd?.noBilling === true,
        skipReason: bd?.noBilling === true ? 'Ei laskutusta' : null,
        buyer: {
          companyName: d.billing?.companyName || member?.name       || null,
          yNumber:     d.billing?.yNumber     || member?.yNumber    || null,
          reference:   d.billing?.reference   || bd?.reference      || null,
          department:  d.billing?.billingDepartment  || bd?.department     || null,
          email:       member?.email                              || null,
        },
        billingFee: !bd?.noBilling && bd?.billingFee != null
          ? Number(bd.billingFee)
          : null,
        deliveries: [],
      };
    }
    groups[key].deliveries.push(d);
  }

  const now = Date.now();

  return Object.values(groups).map((g) => {
    if (g.skipped) {
      return { invoiceData: null, skipped: true, reason: g.skipReason, buyer: g.buyer };
    }

    const rows = g.deliveries.map((d) => ({
      id:        d.deliveryId || d.id,
      date:      d.completedAtMs,
      origin:    d.origin      || '—',
      destination: d.destination || '—',
      unitPrice: Number(d.price || 0),
    }));

    const rowsTotal  = rows.reduce((s, r) => s + r.unitPrice, 0);
    const fee        = g.billingFee ?? 0;
    const net        = rowsTotal + fee;
    const alvAmount  = net * (alvRate / 100);

    return {
      skipped: false,
      invoiceData: {
        invoiceNumber: makeInvoiceNumber('LAS', g.buyer.yNumber),
        issuedAt:      now,
        dueAt:         now + DUE_DAYS * 24 * 60 * 60 * 1000,
        buyer:         g.buyer,
        rows,
        billingFee:    g.billingFee,
        net,
        alvRate,
        alvAmount,
        gross:         net + alvAmount,
      },
    };
  });
}

// ─── Purchase / outgoing to couriers (org → couriers) ────────────────────────

/**
 * Groups completed deliveries by courier.
 *
 * @param {object[]} deliveries  - completed delivery documents
 * @param {object[]} couriers    - member documents (paymentDetails, iban, etc.)
 * @returns {Array<{ invoiceData: InvoiceData, skipped: boolean, reason: string }>}
 */
export function buildPurchaseGroups(deliveries, couriers) {
  const groups = {};

  for (const d of deliveries) {
    const member = couriers.find((m) => m.userId === d.courierId);
    const pd     = member?.paymentDetails;
    const key    = d.courierId || 'unknown';

    if (!groups[key]) {
      groups[key] = {
        skipped:    pd?.noPayment === false,   // noPayment: false = don't pay
        skipReason: pd?.noPayment === false ? 'Ei palkkionmaksua' : null,
        payee: {
          companyName: member?.name    || d.courierName || null,
          yNumber:     member?.yNumber || null,
          iban:        pd?.iban        || null,
          email:       member?.email   || null,
        },
        deliveries: [],
      };
    }
    groups[key].deliveries.push(d);
  }

  const now = Date.now();

  return Object.values(groups).map((g) => {
    if (g.skipped) {
      return { invoiceData: null, skipped: true, reason: g.skipReason, payee: g.payee };
    }

    const rows = g.deliveries.map((d) => ({
      id:          d.deliveryId || d.id,
      date:        d.completedAtMs,
      origin:      d.origin      || '—',
      destination: d.destination || '—',
      unitPrice:   Number(d.reward || 0),
    }));

    const net = rows.reduce((s, r) => s + r.unitPrice, 0);

    return {
      skipped: false,
      invoiceData: {
        invoiceNumber: makeInvoiceNumber('OST', g.payee.yNumber),
        issuedAt:      now,
        dueAt:         now + DUE_DAYS * 24 * 60 * 60 * 1000,
        // For purchase invoices the payee is in the buyer slot so renderers can use a uniform shape
        buyer:         g.payee,
        rows,
        billingFee:    null,
        net,
        alvRate:       0,
        alvAmount:     0,
        gross:         net,
      },
    };
  });
}
